import classNames from 'classnames';
import { find, findIndex, head, range, sortBy } from 'lodash';
import React, { Component } from 'react';

import { IBrokerageInfo } from '../../declarations';
import getCurrencyString from '../../helpers/getCurrencyString';
import Responsive from '../../hocs/Responsive';
import PriceLine from '../common/PriceLine';
import PriceLineHeader from '../common/PriceLineHeader';
import GridCarousel from './GridCarousel';
import OfferCard, { IProps as OfferCardProps } from './OfferCard';
import './PriceJustificationStyle.less';

const ADDED_COMP_LINE_HEIGHT = 60;
const OFFER_DOT_R = 15;
const VALUE_RANGE_ROUNDING = 1000;
const VALUE_RANGE_LOWER_PCT = 0.93;
const VALUE_RANGE_UPPER_PCT = 1.03;

interface IPriceJustificationOffer extends OfferCardProps {
  description: string;
  isPresented?: boolean;
}

interface IState {
  selectedIndex: number;
  sortedOffers: IPriceJustificationOffer[];
}

export interface IProps {
  brokerageInfo: IBrokerageInfo;
  offers: IPriceJustificationOffer[];
}

const getValueRange = offers => {
  // Use opendoor offer if present. Else use ODA offer
  // (Brokerage offer is created if ODA offer net cents is greater than opendoor offer net cents
  //  Thus, sorting the offers by netCents and getting first will always satisfy above condition)
  const offer = head(sortBy(offers, (offer: IPriceJustificationOffer) => offer.netCents));
  const headlineCents = offer.headlineCents / 100;

  return {
    high:
      Math.round((headlineCents * VALUE_RANGE_UPPER_PCT) / VALUE_RANGE_ROUNDING) *
      VALUE_RANGE_ROUNDING,
    low:
      Math.round((headlineCents * VALUE_RANGE_LOWER_PCT) / VALUE_RANGE_ROUNDING) *
      VALUE_RANGE_ROUNDING,
  };
};

class PriceJustification extends Component<IProps, IState> {
  constructor(props) {
    super(props);

    const sortedOffers = this.sortedOffers();

    this.state = {
      selectedIndex: findIndex(
        sortedOffers,
        (offer: IPriceJustificationOffer) => offer.isPresented,
      ),
      sortedOffers,
    };
  }

  public componentWillReceiveProps(nextProps) {
    // when offers change, re-initialize selected index to presented offer
    // this is mainly to handle otherBids not having been loaded yet
    if (this.props.offers !== nextProps.offers) {
      const sortedOffers = this.sortedOffers(nextProps.offers);
      const selectedIndex = findIndex(
        sortedOffers,
        (offer: IPriceJustificationOffer) => offer.isPresented,
      );

      this.setState({
        selectedIndex,
        sortedOffers,
      });
    }
  }

  public getNormalizedSelectedIndex = selectedIndex => {
    const numOffers = this.props.offers.length;
    return (selectedIndex + numOffers) % numOffers;
  };

  public setSelectedIndex = selectedIndex => {
    this.setState({ selectedIndex: this.getNormalizedSelectedIndex(selectedIndex) });
  };

  public setNextIndex = () => {
    this.setSelectedIndex(this.state.selectedIndex + 1);
  };

  public setPrevIndex = () => {
    this.setSelectedIndex(this.state.selectedIndex - 1);
  };

  public sortedOffers = (offers?: IPriceJustificationOffer[]) => {
    return sortBy(offers || this.props.offers, offer => offer.headlineCents);
  };

  public renderResponsiveChildren = ({ gtMD }) => {
    const offers = this.state.sortedOffers;
    const showCarousel = offers.length > (gtMD ? 2 : 1);
    const numOffers = offers.length;

    let { selectedIndex } = this.state;
    const indexToFocus = selectedIndex;
    let colSize;
    let offset;
    let visibleComps;

    if (gtMD) {
      colSize = 4;

      if (!showCarousel) {
        // offers.length should be 1 or 2 at this point
        colSize = 12 / numOffers;
        offset = 0;
        visibleComps = range(0, numOffers, 1);
      } else if (selectedIndex === 0) {
        offset = 1;
        visibleComps = [selectedIndex, selectedIndex + 1];
      } else if (selectedIndex === numOffers - 1) {
        offset = -1;
        visibleComps = [selectedIndex - 1, selectedIndex];
      } else {
        offset = 0;
        visibleComps = range(selectedIndex - 1, selectedIndex + 2, 1);
      }

      selectedIndex = selectedIndex - 1; // to center slide with selected index
    } else {
      colSize = 12;
      offset = 0;
      visibleComps = [selectedIndex];
    }

    const brokerageName = this.props.brokerageInfo.name;

    const valueRange = getValueRange(offers || this.props.offers);

    const presentedOfferHeadlineCents = find(
      offers,
      (offer: IPriceJustificationOffer) => offer.isPresented,
    ).headlineCents;

    const pricelineCompPoints = offers.map((offer, index) => ({
      dotSize: offer.isPresented && OFFER_DOT_R,
      primary: offer.isPresented,
      priority: offer.isPresented ? offers.length : index,
      value: offer.headlineCents / 100,
    }));

    const offerCards = offers.map((offer, index) => (
      <div className={`col-xs-${colSize}`} key={index}>
        <OfferCard
          feeLabel={offer.feeLabel}
          feePct={offer.feePct}
          headlineCents={offer.headlineCents}
          netCents={offer.netCents}
          highlightText={offer.isPresented ? 'YOUR HIGHEST NET OFFER' : null}
          isFocused={!showCarousel || index === indexToFocus}
          key={index}
          onClick={showCarousel && this.setSelectedIndex.bind(null, index)}
          primary={offer.isPresented}
          title={offer.title}
        >
          {offer.description}
        </OfferCard>
      </div>
    ));

    const offerCardsSection = showCarousel ? (
      <GridCarousel
        colSize={colSize}
        selectedIndex={selectedIndex}
        onRightClick={this.setNextIndex}
        onLeftClick={this.setPrevIndex}
      >
        {offerCards}
      </GridCarousel>
    ) : (
      <div className="row center-xs">{offerCards}</div>
    );

    return (
      <div className="text-center od-price-justification">
        <section className="container">
          <div className="row">
            <div className="col-xs-12 col-md-offset-1 col-md-10 col-lg-offset-2 col-lg-8">
              <h1 className="prose">
                We were able to get you an offer for{' '}
                {getCurrencyString(presentedOfferHeadlineCents)} on your home
              </h1>
              <p>
                {brokerageName}, Opendoor's affiliated brokerage, works with a network of trusted
                buyers. From time to time, a buyer in that network is able to make an offer that's
                higher than what Opendoor could offer. When that happens, {brokerageName} presents
                the trusted buyer's offer to you. Below are the values that some of our Trusted
                Buyers placed on your home, and some estimates you may find helpful based on market
                data and the information you provided about your home.
              </p>
            </div>
          </div>
        </section>
        <section>
          <div className="row">
            <PriceLineHeader
              className="col-xs-12 col-sm-6 col-sm-offset-3"
              titleText="Estimated Home Value"
              lowerBoundDollars={valueRange.low}
              upperBoundDollars={valueRange.high}
            >
              <div className="od-price-justification__description">
                This range is our estimate of what your home could sell for based on the market data
                and the specific details you provided about your home.
              </div>
            </PriceLineHeader>
          </div>
          <div className="od-price-justification__price-line-container container">
            <PriceLine
              className="od-price-justification__price-line"
              comps={pricelineCompPoints}
              valuation={valueRange}
              compLineAddedPx={ADDED_COMP_LINE_HEIGHT}
              offset={offset}
              selectCompFunction={this.setSelectedIndex}
              visibleComps={visibleComps}
            />
          </div>
          <div
            className={classNames({
              container: !showCarousel,
              'od-price-justification__offer-cards-container': showCarousel,
            })}
          >
            {offerCardsSection}
          </div>
        </section>
      </div>
    );
  };

  public render() {
    if (this.props.offers.length === 0) {
      // otherBids still loading
      return null;
    }

    return <Responsive render={this.renderResponsiveChildren} />;
  }
}

export default PriceJustification;
